import { useState, useContext } from "react";
import {
  Button,
  TextField,
  Container,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase";
import { UserContext } from "../providers/userProvider";
import { normalizePhoneNumber } from "../utils";

// Module-level singleton — survives React StrictMode's double-mount
let recaptchaVerifier = null;

export const Login = () => {
  const [step, setStep] = useState("phone"); // 'phone' | 'code'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const { getUser } = useContext(UserContext);
  const navigate = useNavigate();

  const getVerifier = () => {
    if (!recaptchaVerifier) {
      recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
      });
    }
    return recaptchaVerifier;
  };

  const resetRecaptcha = () => {
    try { recaptchaVerifier?.clear(); } catch (_) {}
    recaptchaVerifier = null;
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const normalized = normalizePhoneNumber(phoneNumber);
      console.log("normalized:", normalized);
      setPhoneNumber(normalized);
      const result = await signInWithPhoneNumber(
        auth,
        normalized,
        getVerifier()
      );
      setConfirmationResult(result);
      setStep("code");
    } catch (err) {
      console.error("sendCode error:", err.code, err.message);
      setError(`Error: ${err.code || err.message}`);
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await confirmationResult.confirm(code);
      await getUser(phoneNumber);
      localStorage.setItem("phoneNumber", phoneNumber);
      navigate("/");
    } catch (err) {
      console.error("verifyCode error:", err.code, err.message);
      if (err.code === "auth/invalid-verification-code" || err.code === "auth/code-expired") {
        setError("Invalid or expired code. Please try again.");
      } else {
        setError("Couldn't verify your account. Please try again.");
      }
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const formStyle = {
    display: "flex",
    flexDirection: "column",
    marginTop: "120px",
    gap: "12px",
    width: "320px",
  };

  if (step === "phone") {
    return (
      <Container sx={{ display: "flex", justifyContent: "center" }}>
        <form style={formStyle} onSubmit={handleSendCode}>
          <Typography variant="h6" textAlign="center">
            Welcome to the Whitesides Family Blog
          </Typography>
          <TextField
            variant="outlined"
            value={phoneNumber}
            label="Phone Number"
            placeholder="+12085551234"
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Button variant="contained" type="submit" disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Send Code"}
          </Button>
        </form>
        <div id="recaptcha-container" />
      </Container>
    );
  }

  if (step === "code") {
    return (
      <Container sx={{ display: "flex", justifyContent: "center" }}>
        <Box style={formStyle}>
          <Typography variant="h6" textAlign="center">
            Enter your code
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            Sent to {phoneNumber}
          </Typography>
          <form
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            onSubmit={handleVerifyCode}
          >
            <TextField
              variant="outlined"
              value={code}
              label="Verification Code"
              placeholder="123456"
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
              autoFocus
            />
            {error && (
              <Typography color="error" variant="caption">
                {error}
              </Typography>
            )}
            <Button variant="contained" type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : "Verify"}
            </Button>
          </form>
          <Button
            variant="text"
            size="small"
            onClick={() => setStep("phone")}
            sx={{ color: "text.secondary" }}
          >
            Use a different number
          </Button>
        </Box>
      </Container>
    );
  }

};
